import React, { useState } from 'react';
import { useDaily, useLocalParticipant } from '@daily-co/daily-react';
import {
  MicrophoneIcon,
  VideoCameraIcon,
  ArrowRightOnRectangleIcon,
  ComputerDesktopIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/solid';
import {
  MicrophoneIcon as MicrophoneOffIcon,
  VideoCameraSlashIcon,
} from '@heroicons/react/24/outline';

interface ControlBarProps {
  onLeave: () => void;
  isHost?: boolean;
}

/**
 * Control bar for video session controls
 * Provides mute, camera, screen share, and leave functionality
 */
export const ControlBar: React.FC<ControlBarProps> = ({ onLeave, isHost = false }) => {
  const daily = useDaily();
  const localParticipant = useLocalParticipant();
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  if (!daily || !localParticipant) {
    return null;
  }

  const { audio: isAudioOn, video: isVideoOn } = localParticipant;

  const toggleAudio = () => {
    daily.setLocalAudio(!isAudioOn);
  };

  const toggleVideo = () => {
    daily.setLocalVideo(!isVideoOn);
  };

  const toggleScreenShare = async () => {
    try {
      if (isScreenSharing) {
        await daily.stopScreenShare();
        setIsScreenSharing(false);
      } else {
        await daily.startScreenShare();
        setIsScreenSharing(true);
      }
    } catch (error) {
      console.error('Error toggling screen share:', error);
    }
  };

  const handleLeave = () => {
    if (window.confirm('Are you sure you want to leave the session?')) {
      onLeave();
    }
  };

  return (
    <div className="bg-gray-800 border-t border-gray-700 px-6 py-4">
      <div className="max-w-4xl mx-auto flex items-center justify-between">
        {/* Left side - Media controls */}
        <div className="flex items-center space-x-4">
          {/* Microphone toggle */}
          <button
            onClick={toggleAudio}
            className={`p-3 rounded-full transition ${
              isAudioOn
                ? 'bg-gray-700 hover:bg-gray-600 text-white'
                : 'bg-red-600 hover:bg-red-700 text-white'
            }`}
            title={isAudioOn ? 'Mute microphone' : 'Unmute microphone'}
          >
            {isAudioOn ? (
              <MicrophoneIcon className="w-6 h-6" />
            ) : (
              <MicrophoneOffIcon className="w-6 h-6" />
            )}
          </button>

          {/* Camera toggle */}
          <button
            onClick={toggleVideo}
            className={`p-3 rounded-full transition ${
              isVideoOn
                ? 'bg-gray-700 hover:bg-gray-600 text-white'
                : 'bg-red-600 hover:bg-red-700 text-white'
            }`}
            title={isVideoOn ? 'Turn off camera' : 'Turn on camera'}
          >
            {isVideoOn ? (
              <VideoCameraIcon className="w-6 h-6" />
            ) : (
              <VideoCameraSlashIcon className="w-6 h-6" />
            )}
          </button>
        </div>

        {/* Center - Screen share and settings */}
        <div className="flex items-center space-x-4">
          {/* Screen share toggle */}
          <button
            onClick={toggleScreenShare}
            className={`px-4 py-2 rounded-lg flex items-center space-x-2 transition ${
              isScreenSharing
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-gray-700 hover:bg-gray-600 text-white'
            }`}
            title={isScreenSharing ? 'Stop sharing' : 'Share screen'}
          >
            <ComputerDesktopIcon className="w-5 h-5" />
            <span className="text-sm font-medium">
              {isScreenSharing ? 'Stop Sharing' : 'Share Screen'}
            </span>
          </button>

          {/* Settings button */}
          <button
            onClick={() => setShowSettings(!showSettings)}
            className="p-3 rounded-full bg-gray-700 hover:bg-gray-600 text-white transition"
            title="Settings"
          >
            <Cog6ToothIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Right side - Leave button */}
        <div>
          <button
            onClick={handleLeave}
            className="px-6 py-3 rounded-lg bg-red-600 hover:bg-red-700 text-white font-medium flex items-center space-x-2 transition"
            title="Leave session"
          >
            <ArrowRightOnRectangleIcon className="w-5 h-5" />
            <span>{isHost ? 'End Session' : 'Leave'}</span>
          </button>
        </div>
      </div>

      {/* Settings panel */}
      {showSettings && (
        <SettingsPanel onClose={() => setShowSettings(false)} />
      )}
    </div>
  );
};

interface SettingsPanelProps {
  onClose: () => void;
}

/**
 * Settings panel for audio/video device selection
 */
const SettingsPanel: React.FC<SettingsPanelProps> = ({ onClose }) => {
  const daily = useDaily();
  const [devices, setDevices] = useState<{
    cameras: MediaDeviceInfo[];
    microphones: MediaDeviceInfo[];
    speakers: MediaDeviceInfo[];
  }>({
    cameras: [],
    microphones: [],
    speakers: [],
  });

  React.useEffect(() => {
    if (!daily) return;

    const loadDevices = async () => {
      try {
        const result = await daily.enumerateDevices();
        const devices = result.devices || [];
        setDevices({
          cameras: devices.filter(d => d.kind === 'videoinput'),
          microphones: devices.filter(d => d.kind === 'audioinput'),
          speakers: devices.filter(d => d.kind === 'audiooutput'),
        });
      } catch (error) {
        console.error('Error loading devices:', error);
      }
    };

    loadDevices();
  }, [daily]);

  const handleCameraChange = (deviceId: string) => {
    daily?.setInputDevicesAsync({ videoDeviceId: deviceId });
  };

  const handleMicrophoneChange = (deviceId: string) => {
    daily?.setInputDevicesAsync({ audioDeviceId: deviceId });
  };

  const handleSpeakerChange = (deviceId: string) => {
    daily?.setOutputDeviceAsync({ outputDeviceId: deviceId });
  };

  return (
    <div className="absolute bottom-20 right-6 w-80 bg-gray-800 rounded-lg shadow-xl border border-gray-700 p-4 z-50">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-white font-semibold">Settings</h3>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-white transition"
        >
          ✕
        </button>
      </div>

      <div className="space-y-4">
        {/* Camera selection */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Camera
          </label>
          <select
            className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
            onChange={(e) => handleCameraChange(e.target.value)}
          >
            {devices.cameras.map((device) => (
              <option key={device.deviceId} value={device.deviceId}>
                {device.label || 'Camera'}
              </option>
            ))}
          </select>
        </div>

        {/* Microphone selection */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Microphone
          </label>
          <select
            className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
            onChange={(e) => handleMicrophoneChange(e.target.value)}
          >
            {devices.microphones.map((device) => (
              <option key={device.deviceId} value={device.deviceId}>
                {device.label || 'Microphone'}
              </option>
            ))}
          </select>
        </div>

        {/* Speaker selection */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Speaker
          </label>
          <select
            className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
            onChange={(e) => handleSpeakerChange(e.target.value)}
          >
            {devices.speakers.map((device) => (
              <option key={device.deviceId} value={device.deviceId}>
                {device.label || 'Speaker'}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};
